"use client";

import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, ArrowRight } from "lucide-react";
import Link from "next/link";

interface CalculatorProps {
  lang: string;
  config: any; // MasterJSON.calc_config
}

/**
 * CONFIGURATION FOR EASY TUNING
 */
const UI_CONFIG = {
  leftBlock: {
    padding: "p-6 md:p-8", // Tighter padding to save height
    spacing: "space-y-4", // Compact vertical spacing
    labelSize: "text-[15px]", // Scaled down slightly to fit block but still "large"
    buttonTextSize: "text-[15px]",
    inputSize: "text-[15px]",
  },
  tablet: {
    offsetY: "lg:mt-[-150px]", // Adjusted offset
    containerWidth: "max-w-[500px]", // ~15% smaller than the previous ~680-700px
    rotation: 3, // Default clockwise rotation
    screen: {
      labelSize: "text-[18px]", // Kept large as requested
      valueSize: "text-[26px]", // Kept large
      priceSize: "text-6xl sm:text-7xl md:text-8xl", // Main price remains huge
    },
  },
};

export default function Calculator({ lang, config }: CalculatorProps) {
  const logic = config?.logic;
  const pageContent =
    config?.page_content?.[lang] || config?.page_content?.["ru"] || {};

  // Tablet rotation from config or default
  const tabletRotation = config?.tabletRotation ?? UI_CONFIG.tablet.rotation;

  // Default values or from config
  const tiers = logic?.tiers || {};
  const multipliers = logic?.multipliers || {};

  // Form State
  const [selectedLang, setSelectedLang] = useState<string>("");
  const [pages, setPages] = useState<number>(1);
  const [complexity, setComplexity] = useState<string>("general");
  const [urgency, setUrgency] = useState<string>("standard");
  const [price, setPrice] = useState<number>(0);

  // Initialize selected language
  useEffect(() => {
    if (tiers.tier1?.langs?.[0]) {
      setSelectedLang(tiers.tier1.langs[0]);
    }
  }, [tiers]);

  // Calculate Price
  useEffect(() => {
    if (!selectedLang || !tiers) return;

    // Find base price for selected language
    let basePrice = 0;
    Object.values(tiers).forEach((tier: any) => {
      if (tier.langs.includes(selectedLang)) {
        basePrice = tier.price;
      }
    });

    const compMult = multipliers.complexity?.[complexity] || 1;
    const urgMult = multipliers.urgency?.[urgency] || 1;

    const total = basePrice * pages * compMult * urgMult;
    setPrice(Math.round(total));
  }, [selectedLang, pages, complexity, urgency, tiers, multipliers]);

  // UI Texts based on locale
  const t = {
    selectLang:
      lang === "ru"
        ? "Выберите язык"
        : lang === "ro"
          ? "Alegeți limba"
          : "Select Language",
    pageCount:
      lang === "ru"
        ? "Количество страниц"
        : lang === "ro"
          ? "Număr de pagini"
          : "Number of Pages",
    complexity:
      lang === "ru"
        ? "Сложность текста"
        : lang === "ro"
          ? "Complexitatea textului"
          : "Text Complexity",
    urgency:
      lang === "ru" ? "Срочность" : lang === "ro" ? "Urgență" : "Urgency",
    totalPrice:
      lang === "ru"
        ? "Предварительная стоимость"
        : lang === "ro"
          ? "Preț estimativ"
          : "Estimated Price",
    orderNow:
      lang === "ru"
        ? "Заказать сейчас"
        : lang === "ro"
          ? "Comandă acum"
          : "Order Now",
    complexities: {
      general: lang === "ru" ? "Общая" : lang === "ro" ? "Generală" : "General",
      legal:
        lang === "ru" ? "Юридическая" : lang === "ro" ? "Juridică" : "Legal",
      medical:
        lang === "ru" ? "Медицинская" : lang === "ro" ? "Medicală" : "Medical",
      technical:
        lang === "ru" ? "Техническая" : lang === "ro" ? "Tehnicь" : "Technical",
    },
    urgencies: {
      standard:
        lang === "ru" ? "Стандартно" : lang === "ro" ? "Standard" : "Standard",
      express: lang === "ru" ? "Срочно" : lang === "ro" ? "Urgent" : "Express",
    },
    currency: lang === "ru" ? "леев" : lang === "ro" ? "MDL" : "MDL",
    summary:
      lang === "ru"
        ? "Итоговый расчет"
        : lang === "ro"
          ? "Calcul final"
          : "Final Calculation",
  };

  const allLangs = useMemo(() => {
    const langs: string[] = [];
    Object.values(tiers).forEach((tier: any) => {
      langs.push(...tier.langs);
    });
    return [...new Set(langs)].sort();
  }, [tiers]);

  // Framer Motion Variants
  const containerVars = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, staggerChildren: 0.1 },
    },
  };

  const itemVars = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVars}
      className="max-w-7xl mx-auto"
    >
      {/* Grid setup with a wider left side (55/45 ratio) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-start">
        {/* Left Side: Inputs */}
        <div className="space-y-5">
          <motion.div
            variants={itemVars}
            className={`bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white relative z-10 w-full ${UI_CONFIG.leftBlock.padding}`}
          >
            <div className={UI_CONFIG.leftBlock.spacing}>
              {/* Language Selection */}
              <div>
                <label
                  className={`font-black text-slate-400 uppercase tracking-widest mb-3 block ${UI_CONFIG.leftBlock.labelSize}`}
                >
                  {t.selectLang}
                </label>
                <div className="flex flex-wrap gap-2">
                  {allLangs.map((l) => (
                    <button
                      key={l}
                      onClick={() => setSelectedLang(l)}
                      className={`px-3.5 py-1.5 rounded-xl font-black transition-all border uppercase tracking-wider ${UI_CONFIG.leftBlock.buttonTextSize} ${
                        selectedLang === l
                          ? "bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/40 scale-105"
                          : "bg-slate-50 border-slate-200 text-slate-500 hover:border-blue-500/50 hover:text-blue-600"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Page Count */}
              <div className="relative py-1">
                <div className="flex justify-between items-end mb-3">
                  <label
                    className={`font-black text-slate-400 uppercase tracking-widest block ${UI_CONFIG.leftBlock.labelSize}`}
                  >
                    {t.pageCount}
                  </label>
                  <span className="text-3xl font-black text-slate-900 tabular-nums">
                    {pages}
                  </span>
                </div>

                <div className="relative h-2">
                  {/* Background Track */}
                  <div className="absolute inset-0 bg-slate-100 rounded-full overflow-hidden" />

                  {/* Active Progress */}
                  <motion.div
                    className="absolute top-0 left-0 h-full bg-blue-600 rounded-full"
                    animate={{ width: `${pages}%` }}
                    transition={{ type: "spring", bounce: 0, duration: 0.1 }}
                  />

                  {/* Diamond Handle (Rhombus) */}
                  <motion.div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white border-[3px] border-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.4)] z-20 pointer-events-none"
                    animate={{ left: `${pages}%` }}
                    style={{ rotate: "45deg" }}
                    transition={{ type: "spring", bounce: 0, duration: 0.1 }}
                  />

                  {/* Invisible Interaction Layer */}
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={pages}
                    onChange={(e) => setPages(parseInt(e.target.value))}
                    className="absolute inset-[-10px] w-[calc(100%+20px)] h-[calc(100%+20px)] opacity-0 cursor-pointer z-30"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5 pt-1">
                {/* Complexity */}
                <div>
                  <label
                    className={`font-black text-slate-400 uppercase tracking-widest mb-3 block ${UI_CONFIG.leftBlock.labelSize}`}
                  >
                    {t.complexity}
                  </label>
                  <div className="relative">
                    <select
                      value={complexity}
                      onChange={(e) => setComplexity(e.target.value)}
                      className={`w-full bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/10 transition-all cursor-pointer p-3.5 ${UI_CONFIG.leftBlock.inputSize}`}
                    >
                      {Object.keys(multipliers.complexity || {}).map((c) => (
                        <option key={c} value={c} className="text-slate-900">
                          {(t.complexities as any)[c] || c}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                      <ArrowRight size={14} className="rotate-90" />
                    </div>
                  </div>
                </div>

                {/* Urgency */}
                <div>
                  <label
                    className={`font-black text-slate-400 uppercase tracking-widest mb-3 block ${UI_CONFIG.leftBlock.labelSize}`}
                  >
                    {t.urgency}
                  </label>
                  <div className="flex p-1 bg-slate-50 border border-slate-200 rounded-xl h-[52px]">
                    {Object.keys(multipliers.urgency || {}).map((u) => (
                      <button
                        key={u}
                        onClick={() => setUrgency(u)}
                        className={`flex-1 font-black uppercase tracking-widest rounded-lg transition-all ${UI_CONFIG.leftBlock.buttonTextSize} ${
                          urgency === u
                            ? "bg-white text-blue-600 shadow-sm border border-slate-100"
                            : "text-slate-400 hover:text-slate-600"
                        }`}
                      >
                        {(t.urgencies as any)[u] || u}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Help/Warning Block integrated */}
              {(config?.HelpTitle || pageContent.help_title) && (
                <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl flex items-start gap-4 mt-2">
                  <div className="p-2 bg-blue-50 rounded-xl text-blue-500 shrink-0">
                    <AlertCircle size={22} />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-black text-slate-900 text-[16px] uppercase tracking-tight leading-tight">
                      {config?.HelpTitle || pageContent.help_title}
                    </h4>
                    <p className="text-[14px] text-slate-600 leading-relaxed font-medium">
                      {config?.HelpText || pageContent.help_text}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Right Side: Tablet Result */}
        <div className={`relative group ${UI_CONFIG.tablet.offsetY} z-20`}>
          {/* Backglow matching Hero */}
          <div className="absolute inset-0 bg-blue-600/40 blur-[130px] rounded-full scale-125 animate-pulse pointer-events-none" />

          <motion.div
            animate={{ y: [0, -15, 0], scale: [1, 1.01, 1] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            style={{ rotateZ: tabletRotation }}
            className="relative z-10 w-full flex justify-center"
          >
            <div
              className={`relative w-full ${UI_CONFIG.tablet.containerWidth}`}
            >
              <img
                src="https://api.test.tst.md/uploads/premium_profes_delpmaspu_1b20ecfe51.png"
                alt="Tablet Result"
                className="w-full h-auto drop-shadow-[0_60px_60px_rgba(0,0,0,0.8)]"
              />

              {/* Result Content Overlay */}
              <div className="absolute inset-x-[11%] inset-y-[11%] flex flex-col justify-center items-center text-center">
                <div className="space-y-1 sm:space-y-2 opacity-95 w-full">
                  <span
                    className={`font-black uppercase tracking-[0.5em] text-blue-400 block mb-1 ${UI_CONFIG.tablet.screen.labelSize}`}
                  >
                    {t.summary}
                  </span>
                  <div className="flex items-baseline justify-center gap-2 mb-3 sm:mb-6">
                    <motion.span
                      key={price}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className={`font-black text-white tracking-tighter ${UI_CONFIG.tablet.screen.priceSize}`}
                    >
                      {price}
                    </motion.span>
                    <span className="text-xl sm:text-2xl font-black text-blue-400 uppercase">
                      {t.currency}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 sm:gap-10 text-left w-full max-w-[280px] sm:max-w-[380px] mx-auto py-3 sm:py-6 border-y border-white/10 mt-1 sm:mt-4">
                    <div className="space-y-1.5">
                      <span
                        className={`font-black text-slate-500 uppercase tracking-widest leading-none block ${UI_CONFIG.tablet.screen.labelSize}`}
                      >
                        {t.selectLang}
                      </span>
                      <p
                        className={`font-black text-white uppercase ${UI_CONFIG.tablet.screen.valueSize}`}
                      >
                        {selectedLang}
                      </p>
                    </div>
                    <div className="space-y-1.5 text-right sm:text-left">
                      <span
                        className={`font-black text-slate-500 uppercase tracking-widest leading-none block ${UI_CONFIG.tablet.screen.labelSize}`}
                      >
                        {t.pageCount}
                      </span>
                      <p
                        className={`font-black text-white ${UI_CONFIG.tablet.screen.valueSize}`}
                      >
                        {pages}
                      </p>
                    </div>
                  </div>

                  <Link
                    href={{
                      pathname: `/${lang}/contacts`,
                      query: {
                        lang: selectedLang,
                        pages,
                        price,
                        ref: "calculator",
                      },
                    }}
                    className="mt-4 sm:mt-10 group inline-flex items-center gap-4 px-10 sm:px-14 py-3 sm:py-5 bg-blue-600 text-white rounded-2xl font-black text-xs sm:text-sm uppercase tracking-[0.2em] transition-all hover:scale-105 hover:bg-white hover:text-slate-900 shadow-[0_20px_40px_rgba(37,99,235,0.4)]"
                  >
                    {pageContent.cta_button || t.orderNow}
                    <ArrowRight
                      size={18}
                      className="group-hover:translate-x-2 transition-transform"
                    />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
